import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, Mail, CheckCircle2, MessageSquare } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { CommunityIllustration } from "../components/Illustrations";
import { getUtm, getSourcePage } from "../lib/utm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error("Please enter a valid email.");
    if (!form.message.trim()) return toast.error("Please enter a message.");
    if (!consent) return toast.error("Please provide consent to continue.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form, consent, source_page: getSourcePage(), utm: getUtm(),
      });
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo title="Contact" description="Get in touch with the StUpEco Hub team." path="/contact" />
      <section className="relative pt-32 pb-24 lg:pt-40" data-testid="contact-page">
        <div className="grid-lines absolute inset-0 -z-10 opacity-40" />
        <div className="container-hub">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal><span className="eyebrow"><span className="brand-bar h-1.5 w-1.5 rounded-full" />Contact</span></Reveal>
              <Reveal delay={0.05}>
                <h1 className="font-display mt-5 text-4xl font-semibold leading-[1.02] tracking-[-0.03em] text-[color:var(--ink)] sm:text-5xl">
                  Let's talk about growing together.
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-base leading-relaxed text-[color:var(--ink-soft)]">
                  Questions about the Hub, partnerships or early access? Send us a
                  message and the StUpEco team will get back to you.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-8 hub-card rounded-2xl p-6">
                  <CommunityIllustration className="w-full" />
                  <div className="mt-4 flex items-center gap-3 text-sm text-[color:var(--ink-soft)]">
                    <Mail className="h-4 w-4" /> hello@stupeco.com
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              {done ? (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="hub-card rounded-3xl p-10 text-center" data-testid="contact-success">
                  <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[color:var(--brand-green)]/12 text-[color:var(--brand-green)]">
                    <CheckCircle2 className="h-8 w-8" />
                  </span>
                  <h2 className="font-display mt-6 text-2xl font-semibold text-[color:var(--ink)]">Message received</h2>
                  <p className="mt-3 text-[color:var(--ink-soft)]">Thanks for reaching out — we'll reply to {form.email} soon.</p>
                </motion.div>
              ) : (
                <Reveal delay={0.1}>
                  <form onSubmit={onSubmit} data-testid="contact-form" className="hub-card rounded-3xl p-7 lg:p-10" noValidate>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="c-name">Name <span className="text-[color:var(--brand-red)]">*</span></Label>
                        <Input id="c-name" data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" className="h-11 rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="c-email">Email <span className="text-[color:var(--brand-red)]">*</span></Label>
                        <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@company.com" className="h-11 rounded-xl" />
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="c-company">Company</Label>
                        <Input id="c-company" data-testid="contact-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Optional" className="h-11 rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="c-subject">Subject</Label>
                        <Input id="c-subject" data-testid="contact-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Optional" className="h-11 rounded-xl" />
                      </div>
                    </div>
                    <div className="mt-6 space-y-2">
                      <Label htmlFor="c-message">Message <span className="text-[color:var(--brand-red)]">*</span></Label>
                      <Textarea id="c-message" data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="How can we help?" className="min-h-32 rounded-xl" />
                    </div>
                    <label className="mt-6 flex items-start gap-3 text-sm text-[color:var(--ink-soft)]">
                      <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="contact-consent" className="mt-0.5" />
                      <span>I consent to StUpEco Hub storing my details to respond to this enquiry and send relevant updates.</span>
                    </label>
                    <button type="submit" disabled={submitting} data-testid="contact-submit" className="group mt-8 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-7 py-4 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-70">
                      {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><MessageSquare className="h-4 w-4" /> Send message <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
                    </button>
                  </form>
                </Reveal>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
